
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'

// types
import {
    IndividualTariffPayloadT,
    TariffIndividualT,
    TariffIndividualParamT,
    IndividualTariffParamsPayloadT,
    IndividualTariffAdditionalServicesPayloadT,
    TariffIndividualAdditionalServiceT,
    TariffByIdT,
    StandardParamsByIdT,
    AdditionalByIdT,
} from '@/core/types/billing.types'

// realisations
import { billing } from '@/core/realisations/billing.realisation'

// validations
import useVuelidate from '@vuelidate/core'
import { helpers, required, decimal } from '@vuelidate/validators'

// constants
import {
    IndividualTariffsMain,
    IndividualTariffs,
    StandardParamsColumns,
    StandardParamsRows,
    AdditionalServicesColumns,
} from '@/core/constants/columns/IndividualTariffs.columns'
import {
    INDIVIDUAL_PERCENT_LIST,
    INDIVIDUAL_PRICE_LIST,
    UNIT_IDS_1,
    UNIT_IDS_3,
    OVERLOAD_WEIGHT_TYPES,
    INDIVIDUAL_ADDITIONAL_SERVICES,
} from '@/core/constants/billing.constants'
import { AdditionalServicesEnum } from '@/core/enums/billing.enums'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useRoute } from 'vue-router'

export default defineComponent({
    components: {
        TheButton,
        TheSelect,
        TheTable,
        TheInput,
        TableScrollWrapper,
        PageTitle,
    },
    setup() {
        const notifications = useNotifications()
        const route = useRoute()
        const company_id = Number(route.params.id)

        const state = reactive({
            is_loading: false,
            //
            tariff: null as TariffByIdT[] | null,
            tariff_second: null as StandardParamsByIdT[] | null,
            tariff_third: null as AdditionalByIdT[] | null,
            //
            shipmentCosts: null as TariffIndividualT[] | null,
            standardParams: null as TariffIndividualParamT[] | null,
            //
            IDS_SPECIAL: [
                { typeId: 1, ids: [5, 11, 23] },
                { typeId: 2, ids: [16, 17, 28, 29] },
            ],
            additionalServices:
                INDIVIDUAL_ADDITIONAL_SERVICES as TariffIndividualAdditionalServiceT[],
        })

        const state_form$ = useVuelidate(
            {
                // @ts-ignore
                shipmentCosts: { $each: helpers.forEach({ minPrice: { required, decimal } }) },
                // @ts-ignore
                standardParams: { $each: helpers.forEach({ amount: { required, decimal } }) },
                additionalServices: {
                    // @ts-ignore
                    $each: helpers.forEach({
                        minValue: { decimal },
                        price: { required, decimal },
                    }),
                },
            },
            state
        )

        //

        const editShipmentCosts = () => {
            if (!state.shipmentCosts) return

            let shipment_costs: TariffIndividualT[] = [...state.shipmentCosts]

            shipment_costs = shipment_costs.map((s_c) => {
                const s_c_tmp: TariffIndividualT = { ...s_c }

                const num = state.standardParams?.find(
                    (obj) => obj.name === 'overloadWeight' && obj.typeId === s_c_tmp.typeId
                )

                if (num && num.amount) {
                    if (s_c_tmp.weight === null || s_c_tmp.weight > num.amount) {
                        let s_found = shipment_costs.find(
                            (s) =>
                                s.weight === num?.amount &&
                                s.typeId === num?.typeId &&
                                s.zoneId === s_c_tmp.zoneId
                        )

                        s_c_tmp.cost = Number(s_c_tmp.minPrice)
                        if (s_found) s_c_tmp.minPrice = Number(s_found.minPrice)
                    } else {
                        s_c_tmp.cost = 0
                        s_c_tmp.minPrice = Number(s_c_tmp.minPrice)
                    }
                }

                return s_c_tmp
            })

            return shipment_costs
        }

        const addStandardParams = async () => {
            try {
                let payload: IndividualTariffParamsPayloadT = {
                    companyId: company_id,
                    standardParams: state.standardParams as TariffIndividualParamT[],
                }

                state.is_loading = true

                await billing.editIndividualTariffParams(payload)

                notifications.notify({ title: 'Тариф успешно отредактирован!', type: 'success' })
                // await router.push('/tariffs/individual')
            } catch (error) {
                console.error(error)
                notifications.notify({
                    title: 'При редактировании тарифа произошла ошибка!',
                    type: 'danger',
                })
            } finally {
                state.is_loading = false
            }
        }

        const addAdditionalServices = async () => {
            try {
                let additional_services = state.additionalServices.map((service) => {
                    return {
                        ...service,
                        price: Number(service.price),
                        minValue: Number(service.minValue),
                    }
                })

                let payload: IndividualTariffAdditionalServicesPayloadT = {
                    companyId: company_id,
                    additionalServices: additional_services as TariffIndividualAdditionalServiceT[],
                }

                state.is_loading = true

                await billing.editAdditionalServices(payload)

                notifications.notify({ title: 'Доп. услуги отредактированы!', type: 'success' })
                // await router.push('/tariffs/individual')
            } catch (error) {
                console.error(error)
                notifications.notify({
                    title: 'При редактировании доп. услуг произошла ошибка!',
                    type: 'danger',
                })
            } finally {
                state.is_loading = false
            }
        }

        const onSubmit = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                let shipment_costs_edited = editShipmentCosts()

                let payload: IndividualTariffPayloadT = {
                    companyId: company_id,
                    shipmentCosts: shipment_costs_edited as TariffIndividualT[],
                }

                state.is_loading = true

                await billing.editIndividualTariff(payload)
                await addStandardParams()
                await addAdditionalServices()
            } catch (error) {
                console.error(error)
                notifications.notify({
                    title: 'При редактировании тарифа произошла ошибка!',
                    type: 'danger',
                })
            } finally {
                state.is_loading = false
            }
        }

        const fillShipmentCosts = () => {
            if (!state.shipmentCosts) return

            if (state.tariff && state.tariff.length) {
                state.tariff.forEach((tariff, i) => {
                    if (state.shipmentCosts) {
                        state.shipmentCosts[i].zoneId = tariff.zone.id
                        state.shipmentCosts[i].typeId = tariff.type.id
                        state.shipmentCosts[i].weight = tariff.weight
                        // state.shipmentCosts[i].cost = tariff.cost

                        const ids_obj = state.IDS_SPECIAL.find(
                            (obj) => obj.typeId === tariff.type.id
                        )
                        if (ids_obj && ids_obj.ids.includes(i)) {
                            state.shipmentCosts[i].minPrice = tariff.cost
                        } else {
                            state.shipmentCosts[i].minPrice = tariff.minimumPrice
                        }
                    }
                })
            }
        }

        const fillInitialArray = () => {
            const s_c_blank = { zoneId: 1, typeId: 1, weight: null, minPrice: null, cost: 0 }

            state.shipmentCosts = [...new Array(30).fill({ ...s_c_blank })]

            state.shipmentCosts = state.shipmentCosts.map((s_c, i) => {
                const s_c_tmp: TariffIndividualT = { ...s_c }

                // zoneId
                if (5 < i && i < 18) {
                    s_c_tmp.zoneId = 2
                } else if (i > 17) {
                    s_c_tmp.zoneId = 3
                }

                // typeId
                if ((11 < i && i < 18) || i > 23) s_c_tmp.typeId = 2

                // weight
                if (i % 6 === 0) s_c_tmp.weight = 0.5
                else if (i % 6 === 1) s_c_tmp.weight = 1
                else if (i % 6 === 2) s_c_tmp.weight = 2
                else if (i % 6 === 3) s_c_tmp.weight = 5
                else if (i % 6 === 4) s_c_tmp.weight = 10

                return s_c_tmp
            })

            fillShipmentCosts()
        }

        const fillStandardParams = () => {
            if (!state.standardParams) return

            if (state.tariff_second && state.tariff_second.length) {
                state.tariff_second.forEach((tariff, i) => {
                    if (state.standardParams) {
                        state.standardParams[i].amount = tariff.amount
                    }
                })
            }
        }

        const fillParamsArray = () => {
            let params_blank: TariffIndividualParamT = {
                name: '',
                description: '',
                amount: null,
                unitId: 2,
                typeId: 1,
            }

            state.standardParams = [...new Array(26).fill({ ...params_blank })]

            state.standardParams = state.standardParams.map((param, i) => {
                let param_tmp: TariffIndividualParamT = { ...param }
                let index_row = Math.floor(i / 2)

                // name & description
                param_tmp.name = StandardParamsRows[index_row].name
                param_tmp.description = StandardParamsRows[index_row].description

                // type_id
                param_tmp.typeId = (i % 2) + 1

                // amount
                if (param_tmp.name === 'overloadWeight') {
                    param_tmp.amount = param_tmp.typeId === 1 ? 10 : 5
                }

                // amount
                if (param_tmp.name === 'cubSize') {
                    param_tmp.amount = 200
                }

                // unit_id
                if (UNIT_IDS_1.includes(param_tmp.name)) {
                    param_tmp.unitId = 1
                } else if (UNIT_IDS_3.includes(param_tmp.name)) {
                    param_tmp.unitId = 3
                }

                return param_tmp
            })

            fillStandardParams()
        }

        const getIndex = (key: string) => {
            let key_arr: string[] = key.split('-')

            let key_right = Number(key_arr[1])
            let key_left = Number(key_arr[0])

            return key_left + (key_right - 1) * 6 - 1
        }

        const getIndexTitle = (key: string) => {
            let key_arr: string[] = key.split('-')

            let key_right = Number(key_arr[1])

            return key_right - 1
        }

        const getRowIndex = (key: string) => {
            return key.split('-')[0]
        }

        const getParamsIndex = (key: string) => {
            let key_arr: string[] = key.split('-')

            let key_right = Number(key_arr[1])
            let key_left = Number(key_arr[0])

            let index = key_left + (key_right - 1) * 13

            return index < 13 ? index * 2 : index - (26 - index) + 1
        }

        const getPlaceholder = (index: number, typeId: number) => {
            if (!typeId) return

            let ids_obj = state.IDS_SPECIAL.find((obj) => obj.typeId === typeId)

            return ids_obj && ids_obj.ids.includes(index) ? 'Цена (за кг)' : 'Цена'
        }

        const onParamsInput = (index: number) => {
            if (state.standardParams && index % 2 === 0) {
                state.standardParams[index + 1].amount = state.standardParams[index].amount
            }
        }

        const getSign = (name: string) => {
            if (INDIVIDUAL_PERCENT_LIST.includes(name)) return '(%)'
            else if (INDIVIDUAL_PRICE_LIST.includes(name)) return '(₸)'
            else if (name === 'overloadWeight') return '(кг)'
            else return ''
        }

        const onOverloadChange = (value: number, typeId: number) => {
            let ids_obj = state.IDS_SPECIAL.find((obj) => obj.typeId === typeId)

            if (!ids_obj) return

            ids_obj.ids = []

            state.shipmentCosts?.forEach((s_c, idx) => {
                if (
                    ids_obj &&
                    s_c.typeId === typeId &&
                    (s_c.weight === null || s_c.weight > value)
                ) {
                    ids_obj.ids.push(idx)
                }
            })
        }

        const fillAdditional = () => {
            if (!state.tariff_third) return

            state.tariff_third.forEach((tariff, i) => {
                const tariffFound = state.additionalServices.find((s) => s.name === tariff.name)

                if (!tariffFound) return

                tariffFound.minValue = tariff.minValue
                tariffFound.price = tariff.price
            })
        }

        const fillTariffInfo = () => {
            fillInitialArray()
            fillParamsArray()
            fillAdditional()
        }

        const loadIndividualTariff = async () => {
            try {
                state.is_loading = true

                const response = await billing.getTariffByCompanyId(company_id)
                state.tariff = response.data

                const response_second = await billing.getTariffSecondByCompanyId(company_id)
                state.tariff_second = response_second.data

                const response_third = await billing.getTariffThirdByCompanyId({
                    companyId: company_id,
                    limit: 9999,
                    shipmentTariffId: 2,
                })

                if (response_third) {
                    state.tariff_third = response_third.data.filter(
                        (t) => t.name !== 'manipulator' && t.name !== 'crane'
                    )
                }

                fillTariffInfo()
            } catch (error) {
                console.log(error)
                notifications.notify({
                    title: 'При загрузке тарифа произошла ошибка!',
                    type: 'danger',
                })
            } finally {
                state.is_loading = false
            }
        }

        // computed

        const title = computed(() => {
            return state.tariff && state.tariff[0]
                ? `Редактирование тарифа (${state.tariff[0].companyName})`
                : 'Редактирование тарифа'
        })

        //

        onBeforeMount(loadIndividualTariff)

        //

        return {
            state,
            //
            state_form$,
            //
            onSubmit,
            //
            IndividualTariffsMain,
            IndividualTariffs,
            StandardParamsColumns,
            AdditionalServicesColumns,
            StandardParamsRows,
            //
            getIndex,
            getIndexTitle,
            getRowIndex,
            getPlaceholder,
            //
            getParamsIndex,
            onParamsInput,
            getSign,
            onOverloadChange,
            //
            title,
            //
            OVERLOAD_WEIGHT_TYPES,
            AdditionalServicesEnum,
        }
    },
})
